// ─────────────────────────────────────────────────────────────────────────────
// 🔑 EACH ACCOUNT IS ITS OWN CARD, WITH ITS ASKS INSIDE IT (Hannah, 2026-11-01 — the step-5 Assets correction).
// "Which accounts are you using?" lists one card per account the borrower added (borrowerAssetRows — never a
// staff-applied row): its header ("Checking or savings · Frost Bank ••4417"), its own verified amount, its statements,
// "Add another statement", and every request about that account NESTED in the card — a deposit on the account that
// received it, a missing page on the account whose statement is incomplete. No request about an account is ever shown
// loose below the list.
//
// The link is the server's (backend utils/needAccountLink, stored with the request when the check raises it — the
// statement the check named, the account it names, the account just added on step 8, the only account, else the one
// whose statement came in last). A request raised before the link existed (`aboutAccount` with no `accountId`) is placed
// by the same last two rules here, so it is never loose either.
//
// One card shows "Needed" while it has an open request (owed — not yet answered), and the Assets section drops below
// 100% (constants/guidedRemaining withAccountAsks). The figures reconcile: the cards' own verified amounts, plus what
// our team verified from the borrower's documents outside any one account (a staff-applied row, hidden from the list),
// equal the verified total — the total line says so in the borrower's words when there is such an amount.
// ─────────────────────────────────────────────────────────────────────────────
import { borrowerAssetRows } from './assetRows';
import { docMatches } from './sectionModel';

const r0 = (n) => Math.round(Number(n) || 0);

/** A request the borrower still owes (answered = 'responded' waits for the next check). */
export const askOpen = (n) => !!n && !['responded', 'resolved', 'dismissed', 'withdrawn', 'closed'].includes(String(n.status || 'pending'));

/**
 * The account a request sits in, or null when it is not about an account (or the borrower lists none).
 * @param {object} n  a step-5 need (server: accountId, aboutAccount)
 * @param {{ accounts: object[], addedAccount?: { assetId, ids:Set<number> }|null, newestFirst?: string[] }} o
 */
export function accountOfNeed(n, { accounts = [], addedAccount = null, newestFirst = [] } = {}) {
  if (!n || n.ask) return null;
  const listed = (id) => id != null && accounts.some((a) => String(a.id) === String(id));
  if (listed(n.accountId)) return String(n.accountId);
  const added = addedAccount && addedAccount.ids && addedAccount.ids.has(Number(n.id)) && listed(addedAccount.assetId) ? String(addedAccount.assetId) : null;
  if (added) return added;
  if (!n.aboutAccount && n.accountId == null) return null;
  // A request about an account with no listed link: the only account, else the one whose statement came in last,
  // else the last one added — never loose.
  if (!accounts.length) return null;
  if (accounts.length === 1) return String(accounts[0].id);
  const newest = (newestFirst || []).find((id) => listed(id));
  return newest != null ? String(newest) : String(accounts[accounts.length - 1].id);
}

/** Account ids, newest statement first (from the payload's documents: asset_<id>, the highest document id first). */
export function accountsByNewestStatement(accounts, documents) {
  const docs = Array.isArray(documents) ? documents : [];
  return (Array.isArray(accounts) ? accounts : [])
    .map((a) => ({ id: String(a.id), newest: Math.max(0, ...docs.filter((d) => docMatches(d, `asset_${a.id}`) && !d.replaced_by_document_id).map((d) => Number(d.id) || 0)) }))
    .filter((x) => x.newest > 0)
    .sort((x, y) => y.newest - x.newest)
    .map((x) => x.id);
}

/**
 * The account cards for step 5's Assets, from the GET /step5 payload.
 * @param {object} payload  { assetSources, documents, needs, assetFigures: { verified, bySource }, askState, jointAssets }
 * @param {object[]} needs  the needs this step lists (needsOnStep)
 * @returns {{ cards: Array<{ id, account, asks:object[], open:boolean, verified:number|null, entered:number, statements:object[] }>,
 *   linked: Set<number>, anyOpen:boolean, cardsTotal:number, teamVerified:number, verified:number|null }}
 */
export function accountCards(payload, needs) {
  const p = payload || {};
  const accounts = borrowerAssetRows(p.assetSources);
  const a0 = p.askState && p.askState.addedAccount;
  const addedAccount = a0 && a0.assetId != null ? { ...a0, ids: new Set([Number(a0.needId), ...(a0.requestIds || []).map(Number)]) } : null;
  const newestFirst = accountsByNewestStatement(accounts, p.documents);
  const by = {};
  const linked = new Set();
  for (const n of Array.isArray(needs) ? needs : []) {
    const id = accountOfNeed(n, { accounts, addedAccount, newestFirst });
    if (id == null) continue;
    (by[id] = by[id] || []).push(n);
    linked.add(Number(n.id));
  }
  const figures = p.assetFigures || null;
  const bySource = (figures && figures.bySource) || null;
  const docs = Array.isArray(p.documents) ? p.documents : [];
  const cards = accounts.map((a) => {
    const asks = by[String(a.id)] || [];
    return {
      id: String(a.id),
      account: a,
      asks,
      open: asks.some(askOpen),
      verified: bySource ? r0(bySource[a.id]) : null,
      entered: r0(String(a.amount == null ? '' : a.amount).replace(/[^0-9.]/g, '')),
      statements: docs.filter((d) => docMatches(d, `asset_${a.id}`) && !d.replaced_by_document_id),
    };
  });
  const cardsTotal = cards.reduce((t, c) => t + (c.verified || 0), 0);
  const verified = figures && figures.verified != null ? r0(figures.verified) : null;
  // What our team verified outside any one account: a staff-applied row (hidden from the list — its bySource key is its
  // own id, uw_<n>). On a solo file, anything else the cards cannot explain is named the same way, so the total line and
  // the cards always add up. A joint file's total also holds the other applicant's accounts (the joint note says so).
  const hidden = (Array.isArray(p.assetSources) ? p.assetSources : []).filter((s) => s && !accounts.includes(s));
  const hiddenTotal = bySource ? hidden.reduce((t, s) => t + r0(bySource[s.id]), 0) : 0;
  const teamVerified = verified == null ? 0 : p.jointAssets ? hiddenTotal : Math.max(0, verified - cardsTotal);
  return { cards, linked, anyOpen: cards.some((c) => c.open), cardsTotal, teamVerified, verified };
}

/** 🔑 PROPOSED (pending Hannah, 2026-11-01): the total line's second sentence when part of the total is in no account card. */
export const TEAM_VERIFIED_LINE = (amount) => `This includes ${`$${r0(amount).toLocaleString('en-US')}`} our team verified from your documents.`;
/** 🔑 PROPOSED (pending Hannah, 2026-11-01): an account card's amount line. */
export const CARD_VERIFIED_WORD = 'verified';
